import { apiCall } from '../../../services/apiUtil';

// Live scoring actions have no default request timeout upstream in apiCall —
// same rationale as pickleballGameApi.js: fail fast on a degraded connection
// instead of waiting on the browser's own default fetch timeout.
const SCORING_TIMEOUT_MS = 8000;
const withTimeout = (options = {}) => ({ ...options, signal: AbortSignal.timeout(SCORING_TIMEOUT_MS) });

// Volleyball Game API
export const volleyballGameAPI = {
  getById: async (id) => {
    return apiCall(`/games/${id}`);
  },

  getByEventId: async (eventId) => {
    return apiCall(`/games/event/${eventId}`);
  },

  update: async (gameId, updateData) => {
    return apiCall(`/games/${gameId}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
  },

  // Set (or re-pick, after a side-out) the individual serving player.
  setInitialServe: async (gameId, { servingTeam, servingPlayerId }) => {
    return apiCall(`/games/${gameId}/volleyball/serve`, withTimeout({
      method: 'PATCH',
      body: JSON.stringify({ servingTeam, servingPlayerId: servingPlayerId || null }),
    }));
  },

  recordRally: async (gameId, rallyData) => {
    return apiCall(`/games/${gameId}/volleyball/rallies`, withTimeout({
      method: 'POST',
      body: JSON.stringify({
        pointWinner: rallyData.pointWinner,
        scoringPlayerId: rallyData.scoringPlayerId || null,
        shotType: rallyData.shotType || null,
        assistPlayerId: rallyData.assistPlayerId || null,
        errorPlayerId: rallyData.errorPlayerId || null,
      }),
    }));
  },

  undoLastRally: async (gameId) => {
    return apiCall(`/games/${gameId}/volleyball/rallies/last`, {
      method: 'DELETE',
    });
  },

  completeSet: async (gameId, setData) => {
    return apiCall(`/games/${gameId}/volleyball/set-complete`, {
      method: 'POST',
      body: JSON.stringify(setData),
    });
  },

  // Completes the current set by directly setting its final score — skips
  // rally-by-rally scoring entirely (no rallies get recorded).
  completeSetWithScore: async (gameId, { participantAScore, participantBScore }) => {
    return apiCall(`/games/${gameId}/volleyball/final-score`, withTimeout({
      method: 'PATCH',
      body: JSON.stringify({ participantAScore, participantBScore }),
    }));
  },

  // Swaps in a different player for one side of a match mid-set. oldPlayerId
  // identifies which of the side's current players is being replaced;
  // required when the side has more than one player.
  swapPlayer: async (gameId, { side, oldPlayerId = null, newPlayer }) => {
    return apiCall(`/games/${gameId}/volleyball/swap-player`, withTimeout({
      method: 'PATCH',
      body: JSON.stringify({ side, oldPlayerId, newPlayer }),
    }));
  },

  delete: async (gameId) => {
    return apiCall(`/games/${gameId}`, {
      method: 'DELETE',
    });
  },

  pause: async (gameId, { reason, durationMinutes }) => {
    return apiCall(`/games/${gameId}/pause`, {
      method: 'POST',
      body: JSON.stringify({ reason, durationMinutes }),
    });
  },

  resume: async (gameId) => {
    return apiCall(`/games/${gameId}/resume`, { method: 'POST' });
  },
};

export default volleyballGameAPI;
